<script lang="ts">
  import { onMount } from 'svelte';
  import { supabase } from '../lib/supabase';
  import { navigationActions } from '../stores/navigation';
  import { selectedTargetLanguage } from '../stores/flashcards';
  import TargetLanguageDialog from './dialogs/TargetLanguageDialog.svelte';
  import {
    Plus,
    Search,
    BookOpen,
    Archive,
    ArchiveRestore,
    Trash2,
    Library,
  } from 'lucide-svelte';
  import Button from './ui/button.svelte';
  import Card from './ui/card.svelte';
  import { _ } from 'svelte-i18n';
  import { getFlagUrl } from '../lib/languageFlags';
  import { get } from 'svelte/store';

  interface Text {
    id: string;
    title: string;
    content: string;
    language: string;
    url: string | null;
    archived: boolean;
    created_at: string;
    word_count: number;
    tags?: { tag: string }[];
  }

  let texts = $state<Text[]>([]);
  let isLoading = $state(true);
  let searchQuery = $state('');
  let selectedLanguage = $state('');
  let showTargetLanguageModal = $state(false);
  let showArchive = $state(false);

  const t = (key: string, options?: Record<string, unknown>) => get(_)(key, options);

  // Removed: Now using centralized getFlagUrl from lib/languageFlags

  async function loadTexts() {
    isLoading = true;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      let query = supabase
        .from('texts')
        .select('*, tags:text_tags(tag)')
        .eq('user_id', user.id);

      // Apply filters
      if (searchQuery) {
        query = query.or(`title.ilike.%${searchQuery}%,content.ilike.%${searchQuery}%`);
      }

      if (selectedLanguage) {
        query = query.eq('language', selectedLanguage);
      }

      // Filter by archive status
      query = query.eq('archived', showArchive);
      query = query.order('created_at', { ascending: false });

      const { data, error } = await query;

      if (error) {
        console.error('Error loading texts:', error);
        return;
      }

      texts = (data as Text[]) || [];

    } catch (error) {
      console.error('Failed to load texts:', error);
    } finally {
      isLoading = false;
    }
  }

  async function archiveText(id: string, archived: boolean) {
    try {
      const { error } = await supabase
        .from('texts')
        .update({ archived: !archived })
        .eq('id', id);

      if (error) {
        console.error('Error archiving text:', error);
        return;
      }

      await loadTexts();
    } catch (error) {
      console.error('Failed to archive text:', error);
    }
  }

  async function deleteText(id: string, title: string) {
    if (!confirm(t('texts.list.confirm_delete', { values: { title } }))) {
      return;
    }

    try {
      const { error } = await supabase
        .from('texts')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting text:', error);
        return;
      }

      await loadTexts();
    } catch (error) {
      console.error('Failed to delete text:', error);
    }
  }

  function formatDate(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) return t('texts.list.date.today');
    if (days === 1) return t('texts.list.date.yesterday');
    if (days < 7) return t('texts.list.date.days_ago', { values: { count: days } });
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(date);
  }

  function getLanguageName(code: string): string {
    const normalized = code?.toLowerCase() ?? '';
    const key = `language.names.${normalized}`;
    const translated = t(key);
    return translated === key ? code?.toUpperCase() ?? '' : translated;
  }

  onMount(() => {
    loadTexts();
  });

  // Track previous values to detect actual changes
  let prevSearchQuery = $state(searchQuery);
  let prevSelectedLanguage = $state(selectedLanguage);
  let prevShowArchive = $state(showArchive);

  $effect(() => {
    // Only reload when values actually change (not on initial mount)
    const searchChanged = searchQuery !== prevSearchQuery;
    const languageChanged = selectedLanguage !== prevSelectedLanguage;
    const archiveChanged = showArchive !== prevShowArchive;

    if (searchChanged || languageChanged || archiveChanged) {
      prevSearchQuery = searchQuery;
      prevSelectedLanguage = selectedLanguage;
      prevShowArchive = showArchive;
      loadTexts();
    }
  });
</script>

<div class="min-h-screen pb-24 bg-gradient-subtle">
  <div class="mx-auto max-w-md px-4 py-8 space-y-8">
    <!-- Header Card -->
    <Card class="widget-card tone-header">
      <div class="mb-5 flex items-start justify-between gap-4">
        <div>
          <p class="section-label">{$_('language.selected_language')}</p>
          <button
            type="button"
            class="flex mt-2 items-center gap-3 font-semibold leading-tight text-foreground transition hover:text-foreground/90 focus:outline-none"
            onclick={() => (showTargetLanguageModal = true)}
          >
            <div class="h-12 w-16 rounded-2xl bg-foreground/10 shadow-soft-sm overflow-hidden flex items-center justify-center">
              {#if $selectedTargetLanguage}
                <img
                  src={getFlagUrl($selectedTargetLanguage.code, 'w80')}
                  alt={$selectedTargetLanguage.name}
                  class="h-full w-full object-cover"
                  loading="lazy"
                  onerror={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                />
              {/if}
            </div>
            <p class="text-xl font-semibold text-foreground">
              {$selectedTargetLanguage?.name || $_('language.select_language')}
            </p>
          </button>
        </div>
        <div class="rounded-2xl widget-muted px-4 py-3 text-right">
          <p class="section-label text-[0.68rem] tracking-[0.24em]">{$_('texts.list.count_label')}</p>
          <p class="text-2xl font-semibold text-foreground">
            {texts.length}
          </p>
        </div>
      </div>

          <!-- Add Text Button -->
    <button
      type="button"
      class="btn btn-action w-full"
      onclick={() => navigationActions.addText()}
    >
      <Plus class="h-5 w-5 mr-2" />
      {$_('texts.list.add_text')}
    </button>

    <!-- Tab switcher: Active / Archive -->
    <div class="flex rounded-xl bg-foreground/10 p-1 mt-4">
      <button
        type="button"
        onclick={() => { showArchive = false; }}
        class="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 {!showArchive
          ? 'bg-white text-gray-900 shadow-md'
          : 'text-foreground/60 hover:text-foreground hover:bg-white/20'}"
      >
        <Library class="h-4 w-4" />
        {$_('texts.list.tabs.active')}
      </button>
      <button
        type="button"
        onclick={() => { showArchive = true; }}
        class="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 {showArchive
          ? 'bg-white text-gray-900 shadow-md'
          : 'text-foreground/60 hover:text-foreground hover:bg-white/20'}"
      >
        <Archive class="h-4 w-4" />
        {$_('texts.list.tabs.archive')}
      </button>
    </div>

    </Card>

        <!-- Texts Search -->
      <div class="relative flex-1 w-full">
        <input
          type="text"
          bind:value={searchQuery}
          placeholder={$_('texts.list.search_placeholder')}
          class="form-input w-full pr-12"
        />
        <Search class="absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground pointer-events-none" />
      </div>

    <!-- Texts List -->
    {#if isLoading}
      <div class="space-y-3">
        {#each Array(3) as _}
          <Card class="p-4 animate-pulse">
            <div class="h-5 w-2/3 rounded bg-slate-200 mb-2"></div>
            <div class="h-3 w-full rounded bg-slate-100 mb-1"></div>
            <div class="h-3 w-1/2 rounded bg-slate-100"></div>
          </Card>
        {/each}
      </div>
    {:else if texts.length === 0}
      <Card class="widget-card tone-header p-8 text-center">
        {#if showArchive}
          <Archive class="h-12 w-12 mx-auto mb-3 opacity-80" />
          <h3 class="text-lg font-semibold mb-2">
            {$_('texts.list.empty_archive.title')}
          </h3>
          <p class="text-sm opacity-70 mb-4">
            {$_('texts.list.empty_archive.description')}
          </p>
        {:else}
          <BookOpen class="h-12 w-12 mx-auto mb-3 opacity-80" />
          <h3 class="text-lg font-semibold mb-2">
            {$_('texts.list.empty.title')}
          </h3>
          <p class="text-sm opacity-70 mb-4">
            {$_('texts.list.empty.description')}
          </p>
        {/if}
      </Card>
    {:else}
      <div class="space-y-3">
        {#each texts as text (text.id)}
          <Card class="widget-card tone-list p-4 hover:shadow-soft transition-shadow">
            <button
              type="button"
              onclick={() => navigationActions.viewText(text.id)}
              class="w-full text-left group"
            >
              <h3 class="text-base font-semibold mb-1 group-hover:text-primary transition-colors line-clamp-1">
                {text.title}
              </h3>
              <p class="text-sm text-foreground/70 mb-2 line-clamp-1">
                {text.content.substring(0, 100)}...
              </p>
              <div class="flex items-center gap-2 text-xs text-foreground/60 flex-wrap">
                <span>{getLanguageName(text.language)}</span>
                <span>•</span>
                <span>{formatDate(text.created_at)}</span>
              </div>
            </button>

            <div class="flex items-center gap-2 mt-3 pt-3 border-t border-foreground/10">
              <button
                type="button"
                onclick={() => archiveText(text.id, text.archived)}
                class="btn btn-ghost btn-sm flex-1"
                title={text.archived ? $_('texts.list.actions.restore') : $_('texts.list.actions.archive')}
              >
                {#if text.archived}
                  <ArchiveRestore class="h-4 w-4" />
                  {$_('texts.list.actions.restore')}
                {:else}
                  <Archive class="h-4 w-4" />
                  {$_('texts.list.actions.archive')}
                {/if}
              </button>
              <button
                type="button"
                onclick={() => deleteText(text.id, text.title)}
                class="btn btn-destructive btn-sm flex-1"
                title={$_('texts.list.actions.delete')}
              >
                <Trash2 class="h-4 w-4" />
                {$_('texts.list.actions.delete')}
              </button>
            </div>
          </Card>
        {/each}
      </div>
    {/if}
  </div>
</div>

<TargetLanguageDialog isOpen={showTargetLanguageModal} onClose={() => (showTargetLanguageModal = false)} />
